import { FC, useRef, useState, useEffect } from 'react'
import { Tr } from '../../../../_metronic/i18n/Tr'
import type { PlacementOption } from './AddAdvertisement.constants'
import { CLICK_PAYMENT_CONFIG, durationUnitLabel, formatDuration } from './AddAdvertisement.constants'
import { useLang } from '../../../../_metronic/i18n/Metronici18n'
import { toAbsoluteUrl } from '../../../../_metronic/helpers'

interface AddAdvertisementBudgetTabProps {
  paymentSuccess: boolean
  selectedPlacement?: PlacementOption
  isFlat: boolean
  selectedWeeks: number
  customDuration: { value: number; type: 'DAY' | 'WEEK' | 'MONTH' }
  totalAmount: number
  isPriceLoading: boolean
  isPaymentProcessing: boolean
  transactionParam: string
  onSelectWeeks: (weeks: number) => void
  onSelectCustomDuration: (value: number, type: 'DAY' | 'WEEK' | 'MONTH') => void
  onPay: () => void
  onClickPay: () => Promise<boolean>
  onPrevious: () => void
}

const AddAdvertisementBudgetTab: FC<AddAdvertisementBudgetTabProps> = ({
  paymentSuccess,
  selectedPlacement,
  selectedWeeks,
  customDuration,
  totalAmount,
  isPriceLoading,
  isPaymentProcessing,
  transactionParam,
  onSelectWeeks,
  onSelectCustomDuration,
  onClickPay,
  onPrevious,
  onPay,
}) => {
  const t = Tr()
  const lang = useLang()
  const clickFormRef = useRef<HTMLFormElement>(null)

  // Animation state for total amount change
  const [amountAnimKey, setAmountAnimKey] = useState(0)
  useEffect(() => {
    if (!isPriceLoading) setAmountAnimKey((k) => k + 1)
  }, [totalAmount, isPriceLoading])

  // Local state for custom duration input
  const [customType, setCustomType] = useState<'DAY' | 'WEEK' | 'MONTH'>(customDuration.type)
  const [customInputValue, setCustomInputValue] = useState<string>(String(customDuration.value))
  const [customError, setCustomError] = useState<string>('')

  // Sync if parent resets customDuration (e.g., loading existing ad)
  useEffect(() => {
    setCustomType(customDuration.type)
    setCustomInputValue(String(customDuration.value))
  }, [customDuration.value, customDuration.type])

  const handleClickPay = async () => {
    if (isPaymentProcessing || paymentSuccess || isPriceLoading) return
    const ok = await onClickPay()
    if (ok) {
      clickFormRef.current?.submit()
    }
  }

  const clickReturnUrl = `${window.location.origin}/insayr/vendor/viewbanneradvertisment`
  const clickAmount = totalAmount.toFixed(2)

  const basePriceNum = selectedPlacement ? selectedPlacement.amountPerDay : 0
  const formatPrice = (n: number) => n.toLocaleString('uz-UZ') + ' UZS'

  const isCustomSelected = selectedWeeks === 0
  const maxCustom = customType === 'DAY' ? 31 : customType === 'WEEK' ? 52 : 12

  const handleCustomTypeChange = (type: 'DAY' | 'WEEK' | 'MONTH') => {
    setCustomType(type)
    setCustomInputValue('1')
    setCustomError('')
    onSelectCustomDuration(1, type)
  }

  const handleCustomInputChange = (val: string) => {
    setCustomInputValue(val)
    const num = parseInt(val, 10)
    const max = customType === 'DAY' ? 31 : customType === 'WEEK' ? 52 : 12
    if (!val || isNaN(num) || num < 1) {
      setCustomError(t(`Minimum 1 ${customType === 'DAY' ? 'day' : customType === 'WEEK' ? 'week' : 'month'}`))
    } else if (num > max) {
      setCustomError(t(`Maximum ${max} ${customType === 'DAY' ? 'days' : customType === 'WEEK' ? 'weeks' : 'months'}`))
    } else {
      setCustomError('')
      onSelectCustomDuration(num, customType)
    }
  }

  // Price preview for fixed options
  const getFixedPrice = (weeks: number) => {
    const multiplier = weeks === 4 ? 30 : weeks * 7
    return basePriceNum * multiplier
  }

  // Price preview for custom input (local, before API recalculates)
  const customDays = customType === 'DAY'
    ? (parseInt(customInputValue, 10) || 1)
    : customType === 'WEEK'
      ? (parseInt(customInputValue, 10) || 1) * 7
      : (parseInt(customInputValue, 10) || 1) * 30
  const customPreviewPrice = basePriceNum * customDays

  const fixedOptions = [
    { weeks: 1, display: '1', label: durationUnitLabel(1, 'WEEK', lang) },
    { weeks: 2, display: '2', label: durationUnitLabel(2, 'WEEK', lang) },
    { weeks: 4, display: '1', label: durationUnitLabel(1, 'MONTH', lang) },
  ]

  return (
    <>
      <style>{`
        @keyframes amountPop {
          0%   { opacity: 0; transform: scale(0.85) translateY(4px); }
          60%  { opacity: 1; transform: scale(1.08) translateY(-2px); }
          100% { opacity: 1; transform: scale(1) translateY(0); }
        }
      `}</style>
      {paymentSuccess && (
        <div className='alert alert-success d-flex align-items-center mb-4' style={{ borderRadius: '10px' }}>
          <svg width='22' height='22' viewBox='0 0 24 24' fill='none' className='me-3 flex-shrink-0'>
            <circle cx='12' cy='12' r='10' fill='#17c653' />
            <path d='M7 12.5l3.5 3.5 6-7' stroke='#fff' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' />
          </svg>
          <div>
            <div className='fw-bold'>{t('Payment Successful!')}</div>
            <div className='fs-7'>{t('Your ad has been submitted for review. Status: Under Review')}</div>
          </div>
        </div>
      )}

      <div className='card card-flush' style={{ borderRadius: '12px' }}>
        <div className='card-header'>
          <div className='card-title d-flex align-items-center gap-3'>
            <span
              style={{
                width: '42px',
                height: '42px',
                borderRadius: '10px',
                background: '#e8f4fd',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <svg width='22' height='22' viewBox='0 0 24 24' fill='none'>
                <circle cx='12' cy='12' r='9' stroke='#009ef7' strokeWidth='1.8' />
                <path d='M12 7v5l3 3' stroke='#009ef7' strokeWidth='1.8' strokeLinecap='round' />
              </svg>
            </span>
            <div>
              <h2 className='mb-0'>{t('Budget & Payment')}</h2>
              <p className='text-muted fs-7 mb-0'>{t('Set your budget and complete payment')}</p>
            </div>
          </div>
        </div>

        <div className='card-body'>
          <div className='row g-10'>
            <div className='col-lg-7'>
              <div className='fw-semibold text-gray-700 mb-4'>{t('Select Duration')}</div>

              {/* Duration cards — 3 fixed + Custom */}
              <div className='row g-3 mb-4' style={{height: "110px"}}>
                {fixedOptions.map((option) => {
                  const isActive = selectedWeeks === option.weeks
                  return (
                    <div className='col-3' key={option.weeks}>
                      <div
                        onClick={() => { if (!paymentSuccess) onSelectWeeks(option.weeks) }}
                        style={{
                          border: isActive ? '2px solid #009ef7' : '1.5px solid #e4e6ef',
                          borderRadius: '10px',
                          padding: '14px 8px',
                          textAlign: 'center',
                          cursor: paymentSuccess ? 'default' : 'pointer',
                          background: isActive ? '#f0f9ff' : '#fafafa',
                          transition: 'all 0.15s ease',
                          boxShadow: isActive ? '0 2px 12px rgba(0,158,247,0.13)' : 'none',
                        }}
                      >
                        <div className='fw-bold text-gray-900' style={{ fontSize: '20px' }}>{option.display}</div>
                        <div className='text-muted fs-7 mb-1'>{option.label}</div>
                        <div style={{ color: '#009ef7', fontWeight: 600, fontSize: '12px' }}>
                          {formatPrice(getFixedPrice(option.weeks))}
                        </div>
                      </div>
                    </div>
                  )
                })}

                {/* Custom option card */}
                <div className='col-3'>
                  <div
                    onClick={() => { if (!paymentSuccess) { onSelectWeeks(0); onSelectCustomDuration(parseInt(customInputValue, 10) || 1, customType) } }}
                    style={{
                      border: isCustomSelected ? '2px solid #7239ea' : '1.5px solid #e4e6ef',
                      borderRadius: '10px',
                      padding: '14px 8px',
                      textAlign: 'center',
                      cursor: paymentSuccess ? 'default' : 'pointer',
                      background: isCustomSelected ? '#f5f0ff' : '#fafafa',
                      transition: 'all 0.15s ease',
                      boxShadow: isCustomSelected ? '0 2px 12px rgba(114,57,234,0.13)' : 'none',
                      height: '100%',
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '4px' }}>
                      <svg width='20' height='20' viewBox='0 0 24 24' fill='none'>
                        <circle cx='12' cy='12' r='9' stroke={isCustomSelected ? '#7239ea' : '#a1a5b7'} strokeWidth='1.8' />
                        <path d='M12 8v4M12 16h.01' stroke={isCustomSelected ? '#7239ea' : '#a1a5b7'} strokeWidth='2' strokeLinecap='round' />
                      </svg>
                    </div>
                    <div className='fw-bold' style={{ fontSize: '13px', color: isCustomSelected ? '#7239ea' : '#5e6278' }}>{t('Custom')}</div>
                    <div className='text-muted fs-8'>{t('Set range')}</div>
                  </div>
                </div>
              </div>

              {/* Custom duration input — only shown when Custom is selected */}
              {isCustomSelected && (
                <div
                  style={{
                    border: '1.5px solid #cdb4f7',
                    borderRadius: '12px',
                    padding: '20px',
                    background: '#faf5ff',
                    marginBottom: '20px',
                    animation: 'fadeIn 0.2s ease',
                  }}
                >
                  <div className='fw-semibold text-gray-700 mb-3' style={{ fontSize: '14px' }}>{t('Custom Duration')}</div>

                  {/* Day / Week / Month toggle */}
                  <div className='d-flex gap-2 mb-4'>
                    {(['DAY', 'WEEK', 'MONTH'] as const).map((type) => (
                      <button
                        key={type}
                        type='button'
                        disabled={paymentSuccess}
                        onClick={() => handleCustomTypeChange(type)}
                        style={{
                          flex: 1,
                          padding: '9px 0',
                          borderRadius: '8px',
                          fontWeight: 600,
                          fontSize: '14px',
                          border: 'none',
                          cursor: paymentSuccess ? 'default' : 'pointer',
                          background: customType === type ? '#7239ea' : '#ede9fb',
                          color: customType === type ? '#fff' : '#7239ea',
                          transition: 'all 0.15s ease',
                        }}
                      >
                        {type === 'DAY' ? t('Days') : type === 'WEEK' ? t('Weeks') : t('Months')}
                      </button>
                    ))}
                  </div>

                  {/* Numeric input */}
                  <div>
                    <label className='fw-semibold text-gray-700 mb-2 d-block' style={{ fontSize: '13px' }}>
                      {t('Number of')} {customType === 'DAY' ? t('Days') : customType === 'WEEK' ? t('Weeks') : t('Months')}
                      <span className='text-muted fw-normal ms-2' style={{ fontSize: '12px' }}>
                        ({t('min')} 1 – {t('max')} {maxCustom})
                      </span>
                    </label>
                    <div className='d-flex align-items-center gap-3'>
                      {/* Decrement */}
                      <button
                        type='button'
                        disabled={paymentSuccess || parseInt(customInputValue, 10) <= 1}
                        onClick={() => handleCustomInputChange(String(Math.max(1, (parseInt(customInputValue, 10) || 1) - 1)))}
                        style={{
                          width: '36px', height: '36px', borderRadius: '8px', border: '1.5px solid #cdb4f7',
                          background: '#fff', color: '#7239ea', fontWeight: 700, fontSize: '18px',
                          cursor: parseInt(customInputValue, 10) <= 1 || paymentSuccess ? 'default' : 'pointer',
                          opacity: parseInt(customInputValue, 10) <= 1 || paymentSuccess ? 0.4 : 1,
                          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                        }}
                      >−</button>

                      {/* Text input */}
                      <input
                        type='number'
                        min={1}
                        max={maxCustom}
                        disabled={paymentSuccess}
                        value={customInputValue}
                        onChange={(e) => handleCustomInputChange(e.target.value)}
                        style={{
                          width: '80px', textAlign: 'center', borderRadius: '8px',
                          border: customError ? '1.5px solid #f1416c' : '1.5px solid #cdb4f7',
                          padding: '7px 10px', fontSize: '18px', fontWeight: 700,
                          color: '#3f4254', outline: 'none', background: '#fff',
                        }}
                      />

                      {/* Increment */}
                      <button
                        type='button'
                        disabled={paymentSuccess || parseInt(customInputValue, 10) >= maxCustom}
                        onClick={() => handleCustomInputChange(String(Math.min(maxCustom, (parseInt(customInputValue, 10) || 0) + 1)))}
                        style={{
                          width: '36px', height: '36px', borderRadius: '8px', border: '1.5px solid #cdb4f7',
                          background: '#fff', color: '#7239ea', fontWeight: 700, fontSize: '18px',
                          cursor: parseInt(customInputValue, 10) >= maxCustom || paymentSuccess ? 'default' : 'pointer',
                          opacity: parseInt(customInputValue, 10) >= maxCustom || paymentSuccess ? 0.4 : 1,
                          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                        }}
                      >+</button>

                      <span className='text-muted fs-7'>
                        = {formatDuration(customDays, 'DAY', lang)}
                      </span>
                    </div>

                    {customError && (
                      <div className='text-danger fs-7 mt-2'>{customError}</div>
                    )}
                  </div>
                </div>
              )}

              <div className='separator mb-7' />

              <div className='fw-semibold text-gray-700 mb-4'>{t('Payment Summary')}</div>
              <div className='d-flex justify-content-between py-2 border-bottom'>
                <span className='text-muted'>{t('Placement')}:</span>
                <span className='fw-semibold text-gray-800 text-end' style={{ maxWidth: '60%' }}>
                  {selectedPlacement?.placementName || '—'}
                </span>
              </div>
              <div className='d-flex justify-content-between py-2 border-bottom'>
                <span className='text-muted'>{t('Pricing Model')}:</span>
                <span className='fw-semibold text-gray-800'>{t('Per Day')}</span>
              </div>
              <div className='d-flex justify-content-between py-2 border-bottom'>
                <span className='text-muted'>{t('Duration')}:</span>
                <span className='fw-semibold text-gray-800'>
                  {isCustomSelected
                    ? formatDuration(customDays, 'DAY', lang)
                    : selectedWeeks === 4
                      ? formatDuration(30, 'DAY', lang)
                      : formatDuration(selectedWeeks * 7, 'DAY', lang)}
                </span>
              </div>
              <div className='d-flex justify-content-between align-items-center pt-3 mb-7' style={{ height: '40px' }}>
                <span className='fw-bold text-gray-900' style={{ fontSize: '16px' }}>{t('Total Amount')}:</span>
                {isPriceLoading ? (
                  <span className='text-muted fs-7'>{t('Calculating...')}</span>
                ) : (
                  <span
                    key={amountAnimKey}
                    style={{
                      color: '#009ef7',
                      fontWeight: 700,
                      fontSize: '22px',
                      display: 'inline-block',
                      animation: 'amountPop 0.4s ease',
                    }}
                  >
                    {formatPrice(totalAmount)}
                  </span>
                )}
              </div>

              <div className='separator mb-7' />

              <div className='fw-semibold text-gray-700 mb-4'>{t('Payment Method')}</div>

              {/* Click payment method card */}
              <div className='d-flex justify-content-between w-100'>
                <div style={{ width: '48%' }}>
                  <div
                    style={{
                      border: '2px solid #00AAFF',
                      borderRadius: '10px',
                      padding: '14px 16px',
                      background: '#f0f9ff',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      marginBottom: '20px',
                    }}
                  >
                    <img src={toAbsoluteUrl('/media/logos/Abstract blue rings in a square pattern.png')} alt='' height={70} style={{ margin: '-14px' }} />
                    <div>
                      <div className='fw-bold' style={{ color: '#00AAFF', fontSize: '15px' }}>Click</div>
                      <div className='text-muted fs-7'>{t('Pay securely via Click')}</div>
                    </div>
                  </div>

                  <form
                    ref={clickFormRef}
                    action='https://my.click.uz/services/pay'
                    method='get'
                    target='_self'
                    style={{ display: 'none' }}
                  >
                    <input type='hidden' name='amount' value={clickAmount} />
                    {/* clickAmount */}
                    <input type='hidden' name='merchant_id' value={CLICK_PAYMENT_CONFIG.merchantId} />
                    <input type='hidden' name='merchant_user_id' value={CLICK_PAYMENT_CONFIG.merchantUserId} />
                    <input type='hidden' name='service_id' value={CLICK_PAYMENT_CONFIG.serviceId} />
                    <input type='hidden' name='transaction_param' value={transactionParam} />
                    <input type='hidden' name='return_url' value={clickReturnUrl} />
                  </form>

                  <button
                    type='button'
                    className='btn w-100'
                    disabled={isPaymentProcessing || paymentSuccess || isPriceLoading || (isCustomSelected && !!customError)}
                    // onClick={handleClickPay}
                    onClick={onPay}
                    style={{
                      background: '#00AAFF',
                      color: '#fff',
                      fontWeight: 600,
                      borderRadius: '8px',
                      fontSize: '15px',
                      padding: '12px',
                      opacity: isPaymentProcessing || paymentSuccess || isPriceLoading || (isCustomSelected && !!customError) ? 0.7 : 1,
                    }}
                  >
                    {isPaymentProcessing ? t('Processing...') : t('Pay with Click')}
                  </button>
                </div>
              </div>
            </div>

            <div className='col-lg-5'>
              <div style={{ borderLeft: '1.5px solid #e4e6ef', paddingLeft: '32px', height: '100%' }}>
                <div className='fw-bold text-gray-800 mb-5' style={{ fontSize: '16px', borderBottom: '1.5px solid #e4e6ef', paddingBottom: '12px' }}>
                  {t('Important Information')}
                </div>

                <div
                  style={{
                    background: '#e8f4fd',
                    border: '1px solid #b8dff7',
                    borderRadius: '10px',
                    padding: '14px 16px',
                    marginBottom: '20px',
                  }}
                >
                  <div className='d-flex align-items-center gap-2 mb-2'>
                    <svg width='18' height='18' viewBox='0 0 24 24' fill='none'>
                      <circle cx='12' cy='12' r='10' stroke='#009ef7' strokeWidth='1.8' />
                      <line x1='12' y1='8' x2='12' y2='12' stroke='#009ef7' strokeWidth='2' strokeLinecap='round' />
                      <circle cx='12' cy='16' r='1' fill='#009ef7' />
                    </svg>
                    <span className='fw-bold text-gray-800'>{t('Review Process')}</span>
                  </div>
                  <p className='text-muted fs-7 mb-0'>
                    {t('After payment, your ad will be submitted for review. The admin team will review your ad within 24-48 hours.')}
                  </p>
                </div>

                <div className='mb-5'>
                  <div className='fw-semibold text-gray-800 mb-3'>{t('What happens next?')}</div>
                  <ol className='ps-4 mb-0' style={{ lineHeight: '2' }}>
                    {[
                      t('Your ad will be submitted with status "Under Review"'),
                      t('Admin team reviews your ad content and targeting'),
                      t('If approved, your ad goes live at the selected placement'),
                      t('You can track performance in the Ads dashboard'),
                      t('If rejected, you\'ll receive feedback and can resubmit'),
                    ].map((step, i) => (
                      <li key={i} className='text-gray-700 fs-7'>{step}</li>
                    ))}
                  </ol>
                </div>

                <div
                  style={{
                    background: '#f0fdf4',
                    border: '1px solid #bbf7d0',
                    borderRadius: '10px',
                    padding: '14px 16px',
                  }}
                >
                  <div className='fw-bold text-gray-800 mb-1'>{t('Payment is Secure')}</div>
                  <p className='text-muted fs-7 mb-0'>
                    {t('All payments are processed securely through Click. Your payment information is encrypted and protected.')}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className='d-flex justify-content-end mt-3'>
        <button type='button' className='btn btn-secondary' onClick={onPrevious}>
          {t('Previous')}
        </button>
      </div>
    </>
  )
}

export default AddAdvertisementBudgetTab
